import type { FieldPath, UseFormReturn } from 'react-hook-form';

import type { RevenueLockdownSchema } from '@/_pages/settings/revenue-lockdowns/shared';
import { useGetRevenueLockdowns } from '@/_pages/settings/revenue-lockdowns/shared';
import { dateTime, defaultApiParamStr } from '@/_shared';

import type { AgreementFormSchema } from './schemas';

export const REVENUE_LOCKDOWN_BLOCKED_MESSAGE =
  'This date range is locked due to finalized financial reporting. Revenue for this period has already been reported and cannot be altered. [OVERRIDE]';

export const REVENUE_LOCKDOWN_CONFLICT_MESSAGE = 'This date conflicts with a locked down financial period';
export const REVENUE_LOCKDOWN_NO_OVERRIDE_PERMISSION_MESSAGE =
  'The changes you are attempting conflict with a locked down financial period';

type DateValue = string | null | undefined;

function toStartMs(date: string): number {
  return dateTime.parse(date).startOf('day').valueOf();
}

function toEndMs(date: string): number {
  return dateTime.parse(date).endOf('day').valueOf();
}

function toRangeMs(start: DateValue, end: DateValue): { startMs: number; endMs: number } | null {
  const s = start ?? null;
  const e = end ?? null;

  if (!s && !e) return null;

  // If only one side is present, treat it as a single-day range.
  if (s && !e) {
    return { startMs: toStartMs(s), endMs: Number.POSITIVE_INFINITY };
  }
  if (!s && e) {
    return { startMs: toStartMs(e), endMs: toEndMs(e) };
  }

  // Both present.
  return { startMs: toStartMs(s as string), endMs: toEndMs(e as string) };
}

function rangesIntersect(a: { startMs: number; endMs: number }, b: { startMs: number; endMs: number }): boolean {
  return a.startMs <= b.endMs && b.startMs <= a.endMs;
}

function lockdownToRange(lockdown: RevenueLockdownSchema): { startMs: number; endMs: number } {
  return {
    startMs: toStartMs(lockdown.lock_start_date),
    endMs: toEndMs(lockdown.lock_end_date),
  };
}

function getIntersectingLockdownIds(
  range: { startMs: number; endMs: number },
  lockdowns: RevenueLockdownSchema[]
): number[] {
  const ids: number[] = [];
  for (const lockdown of lockdowns) {
    const lr = lockdownToRange(lockdown);
    if (rangesIntersect(range, lr)) {
      ids.push(lockdown.id);
    }
  }
  return ids;
}

export function getRevenueLockdownIdsForDateChange(args: {
  lockdowns: RevenueLockdownSchema[];
  oldStart: DateValue;
  oldEnd: DateValue;
  newStart: DateValue;
  newEnd: DateValue;
}): number[] {
  const oldRange = toRangeMs(args.oldStart, args.oldEnd);
  const newRange = toRangeMs(args.newStart, args.newEnd);

  const oldIds = oldRange ? getIntersectingLockdownIds(oldRange, args.lockdowns) : [];
  const newIds = newRange ? getIntersectingLockdownIds(newRange, args.lockdowns) : [];

  return [...new Set([...oldIds, ...newIds])];
}

export function useRevenueLockdownGuard(form: UseFormReturn<AgreementFormSchema>) {
  //!important 500 per_page
  const { data } = useGetRevenueLockdowns(defaultApiParamStr);
  const lockdowns = data?.data ?? [];

  const blockIfLocked = (opts: {
    fieldName: FieldPath<AgreementFormSchema>;
    oldStart: DateValue;
    oldEnd: DateValue;
    newStart: DateValue;
    newEnd: DateValue;
  }): { allowed: true } | { allowed: false; lockdownIds: number[] } => {
    const lockdownIds = getRevenueLockdownIdsForDateChange({
      lockdowns,
      oldStart: opts.oldStart,
      oldEnd: opts.oldEnd,
      newStart: opts.newStart,
      newEnd: opts.newEnd,
    });

    if (lockdownIds.length > 0) {
      form.setError(opts.fieldName, { type: 'manual', message: REVENUE_LOCKDOWN_BLOCKED_MESSAGE });
      return { allowed: false, lockdownIds };
    }

    form.clearErrors(opts.fieldName);
    return { allowed: true };
  };

  const guardSingleDate = (opts: {
    fieldName: FieldPath<AgreementFormSchema>;
    oldValue: DateValue;
    newValue: DateValue;
  }) =>
    blockIfLocked({
      fieldName: opts.fieldName,
      oldStart: opts.oldValue,
      oldEnd: opts.oldValue,
      newStart: opts.newValue,
      newEnd: opts.newValue,
    });

  const guardRangeDates = (opts: {
    fieldName: FieldPath<AgreementFormSchema>;
    oldStart: DateValue;
    oldEnd: DateValue;
    newStart: DateValue;
    newEnd: DateValue;
  }) => blockIfLocked(opts);

  return {
    lockdowns,
    guardSingleDate,
    guardRangeDates,
  };
}
